import type { ActorEvent, BlockAbstract, FilterCommonCondition, IndependentAction } from '@lighthouse/core'
import { BlockType, VariableType } from '@lighthouse/core'
import type { CollisionArea } from '@lighthouse/shared'
import {
    fillSyncBlock,
    type FlowLayoutNode,
    getBlockChildren,
    hasChildrenBlock,
    isButtonBlock,
    isContainerBlock,
    isIconBlock,
    isTextBlock
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { clone } from 'rambda'

import type { NodeIdWithScope } from '@/atoms/page/types'

/**
 * 根据id和tree删除节点
 * @date 2024/1/8 - 13:42:38
 *
 * @export
 * @param {string} id
 * @returns {(tree: BlockAbstract[]) => BlockAbstract[]}
 */
export function removeBlockById(id: string) {
    const recursion = (tree: BlockAbstract[]): undefined | BlockAbstract[] => {
        for (let i = 0; i < tree.length; i++) {
            const block = tree[i]
            if (block.id === id) {
                return tree.splice(i, 1)
            }

            if (block.type === BlockType.container) {
                for (const view of block.children) {
                    const res = recursion(view.children)
                    if (res) {
                        return res
                    }
                }
            }

            const children = getBlockChildren(block)
            if (children) {
                const res = recursion(children)
                if (res) {
                    return res
                }
            }
        }
    }
    return recursion
}

/**
 * over的是否是active本身或者后代，禁止自己插入到自己或者自己的后代
 * @date 2024/3/26 - 15:01:24
 *
 * @export
 * @param {NodeIdWithScope} activeId
 * @param {CollisionArea} overId
 */
export function isActiveInOver(activeId: NodeIdWithScope, over: CollisionArea, blocks: BlockAbstract[], syncComponents: BlockAbstract[]) {
    if (activeId.id === over.scope) {
        return true
    }

    function findOver(id: string, tree: BlockAbstract[]): boolean {
        for (const node of tree) {
            if (node.id === id) {
                return true
            }

            const filledBlock = fillSyncBlock(node, syncComponents)
            const children = getBlockChildren(filledBlock)
            if (children) {
                const res = findOver(id, children)
                if (res) {
                    return res
                }
            }
        }

        return false
    }

    const recursion = (tree: BlockAbstract[]): boolean => {
        for (const node of tree) {
            const filledBlock = fillSyncBlock(node, syncComponents)

            const children = getBlockChildren(filledBlock)

            if (filledBlock.id === activeId.id) {
                if (children) {
                    const res = findOver(over.id, children)
                    if (res) {
                        return true
                    }
                }
                return false
            }

            if (children) {
                const res = recursion(children)
                if (res) {
                    return true
                }
            }
        }

        return false
    }

    return recursion(activeId.scope ? syncComponents : blocks)
}

export interface BlockFilterOptions {
    /** 禁止的block类型列表 */
    notAllowedList: BlockType[]
    /** 对于视图类型的自定义过滤函数 */
    viewTypeFilter?: (block: BlockAbstract) => boolean
}

/**
 * 递归检查是否有禁止的node
 *
 * @export
 * @param {BlockAbstract} target
 * @param {BlockFilterOptions} options - 过滤选项
 * @returns {boolean}
 */
export function includeNotAllowedBlock(target: BlockAbstract, options: BlockFilterOptions): boolean {
    const { notAllowedList, viewTypeFilter } = options

    // 检查是否在禁止列表中
    if (notAllowedList.includes(target.type)) {
        // 如果是视图类型且提供了自定义过滤函数，则使用自定义逻辑
        if (target.type === BlockType.view && viewTypeFilter) {
            return viewTypeFilter(target)
        }
        // 其他情况按原逻辑处理
        return true
    }

    const children = getBlockChildren(target)

    if (children) {
        for (const child of children) {
            const res = includeNotAllowedBlock(child, options)
            if (res) {
                return true
            }
        }
    }
    return false
}

/** 递归复制block，替换id */
export function copyBlock<T extends BlockAbstract>(blocks: T[]): T[] {
    const idMap = new Map<string, string>()

    // 递归收集新旧blockId的映射
    function collectId(oldBlock: BlockAbstract) {
        const newId = `${oldBlock.type}-${nanoid(12)}`
        idMap.set(oldBlock.id, newId)

        if (hasChildrenBlock(oldBlock)) {
            if (isContainerBlock(oldBlock)) {
                oldBlock.children.forEach(view => {
                    view.children.forEach(collectId)
                })
                return
            }

            oldBlock.children.forEach(collectId)
        }
    }

    blocks.forEach(collectId)

    // // 重设动作中绑定的组件id
    // function resetActionBindBlockId(action: IndependentAction | undefined) {
    //     const newActionId = nanoid()
    //     if (action) {
    //         action.id = newActionId
    //     }
    //     if (action?.type === 'control' && action.data.control.controlElementId) {
    //         const oldId = action.data.control.controlElementId
    //         if (idMap.has(oldId)) {
    //             action.data.control.controlElementId = idMap.get(oldId) || oldId
    //         }
    //     }

    //     if (action?.type === 'scrollTo' && action.data.scrollTo.scrollToElementId) {
    //         const oldId = action.data.scrollTo.scrollToElementId
    //         if (idMap.has(oldId)) {
    //             action.data.scrollTo.scrollToElementId = idMap.get(oldId) || oldId
    //         }
    //     }
    // }
    // 重设动作中绑定的组件id
    function resetActionBindBlockId(eventList: ActorEvent[] | undefined) {
        if (eventList) {
            eventList.forEach(item => {
                if (item.id) {
                    const newActionId = nanoid()
                    item.id = newActionId
                    item.actions.forEach(action => {
                        if (action.type === 'control' && action.data.control.controlElementId) {
                            const oldId = action.data.control.controlElementId
                            if (idMap.has(oldId)) {
                                action.data.control.controlElementId = idMap.get(oldId) || oldId
                            }
                        }
                    })
                    item.actions.forEach(action => {
                        if (action.type === 'scrollTo' && action.data.scrollTo.scrollToElementId) {
                            const oldId = action.data.scrollTo.scrollToElementId
                            if (idMap.has(oldId)) {
                                action.data.scrollTo.scrollToElementId = idMap.get(oldId) || oldId
                            }
                        }
                    })
                }
            })
        }
    }
    // BlockType.button, BlockType.card, BlockType.container, BlockType.view, BlockType.icon, BlockType.image, BlockType.tabs
    function recursion(oldBlock: BlockAbstract): BlockAbstract {
        if (oldBlock.type === BlockType.card) {
            oldBlock.config.list.forEach(item => {
                resetActionBindBlockId(item.eventList)
            })
        }

        if (oldBlock.type === BlockType.tabs) {
            oldBlock.config.baseList.forEach(item => {
                resetActionBindBlockId(item.eventList)
            })
        }

        if (oldBlock.type === BlockType.view) {
            oldBlock.config.toolbar?.forEach(item => {
                resetActionBindBlockId(item.eventList)
            })
            oldBlock.config.recordOperator?.forEach(item => {
                resetActionBindBlockId(item.eventList)
            })
            resetActionBindBlockId(oldBlock.config.eventList)

            oldBlock.config.linkFilterController?.expression?.conditions?.forEach(item => {
                const newItem = item as FilterCommonCondition
                if (newItem.idVariable?.type === VariableType.FILTER && newItem.idVariable.filterVariable?.blockId) {
                    const oldId = newItem.idVariable.filterVariable.blockId
                    newItem.idVariable.filterVariable.blockId = idMap.get(oldId) || oldId
                }
            })
        }

        if (oldBlock.type === BlockType.chart) {
            oldBlock.config.linkFilterController?.expression?.conditions?.forEach(item => {
                const newItem = item as FilterCommonCondition
                if (newItem.idVariable?.type === VariableType.FILTER && newItem.idVariable.filterVariable?.blockId) {
                    const oldId = newItem.idVariable.filterVariable.blockId
                    newItem.idVariable.filterVariable.blockId = idMap.get(oldId) || oldId
                }
            })
        }

        if (oldBlock.type === BlockType.container) {
            resetActionBindBlockId(oldBlock.config.eventList)

            if ((oldBlock.config.floatPointerList ?? []).length > 0) {
                const oldIds = oldBlock.config.floatPointerList
                oldBlock.config.floatPointerList = oldIds?.map(oldId => (idMap.has(oldId) ? idMap.get(oldId) || oldId : oldId))
            }

            return {
                ...oldBlock,
                id: idMap.get(oldBlock.id) || oldBlock.id,
                children: oldBlock.children.map(view => {
                    return {
                        ...view,
                        children: view.children.map(recursion)
                    }
                })
            }
        }

        if (isTextBlock(oldBlock) || isIconBlock(oldBlock) || isButtonBlock(oldBlock)) {
            resetActionBindBlockId(oldBlock.config.eventList)
            const oldIds = oldBlock.config.floatPointerList
            oldBlock.config.floatPointerList = oldIds?.map(oldId => (idMap.has(oldId) ? idMap.get(oldId) || oldId : oldId))
        }

        if (hasChildrenBlock(oldBlock)) {
            return { ...oldBlock, id: idMap.get(oldBlock.id) || oldBlock.id, children: oldBlock.children.map(recursion) }
        }

        return { ...oldBlock, id: idMap.get(oldBlock.id) || oldBlock.id }
    }

    return blocks.map(block => recursion(clone(block))) as T[]
}

/** 将block转换为一个同步组件 */
export function transformBlock2SyncComponents<T extends BlockAbstract>(block: T, isMaster = true): T {
    function recursion(b: BlockAbstract, _isMaster?: boolean): BlockAbstract {
        // // master的id需要更新一下，子级元素尽可能保留相同的id，
        // // 这样创建出来的同步block实例内的子级id和之前一致。
        // const newId = _isMaster ? `${b.type}-${nanoid(12)}` : b.id

        if (b.type === BlockType.container) {
            return {
                ...b,
                // id: newId,
                isMasterSynchronous: _isMaster,
                isLeafSynchronous: !_isMaster,
                children: b.children.map(view => {
                    return {
                        ...view,
                        children: view.children.map(item => recursion(item))
                    }
                })
            }
        }

        if (hasChildrenBlock(b)) {
            return {
                ...b,
                // id: newId,
                isMasterSynchronous: _isMaster,
                isLeafSynchronous: !_isMaster,
                children: b.children.map(item => recursion(item))
            }
        }

        // if (b.type === BlockType.view && b.config.viewType === 'custom' && b.children) {
        //     return {
        //         ...b,
        //         // id: newId,
        //         isMasterSynchronous: _isMaster,
        //         isLeafSynchronous: !_isMaster,
        //         children: b.children.map(item => recursion(item))
        //     }
        // }

        return { ...b, /* id: newId, */ isMasterSynchronous: _isMaster, isLeafSynchronous: !_isMaster }
    }

    return recursion(block, isMaster) as T
}
